import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, RefreshCw, KeyRound, Terminal } from 'lucide-react';
import api from '@/lib/api';
import { Card } from '@/components/shared';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminApi() {
  const [info, setInfo] = useState(null);

  const load = () => api.get('/admin/pos/info').then((r) => setInfo(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('Kopyalandı');
  };

  const rotate = async (storeId, storeName) => {
    if (!window.confirm(`${storeName} mağazasının terminal token'ı yenilenecek. Mevcut POS cihazları yeni token ile güncellenmezse satış oluşturamaz. Devam edilsin mi?`)) return;
    try { await api.post(`/admin/stores/${storeId}/rotate-token`); toast.success('Terminal token yenilendi'); load(); }
    catch { toast.error('İşlem başarısız'); }
  };

  if (!info) return <div className="text-navy/40 text-sm">Yükleniyor...</div>;

  const endpoint = `${BACKEND}${info.endpointPath}`;
  const sample = `curl -X POST "${endpoint}" \\
  -H "X-API-Key: ${info.apiKey}" \\
  -H "X-Terminal-Token: <MAGAZA_TERMINAL_TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "code": "BM-XXXXXXXX",
    "storeId": "<STORE_ID>",
    "basketAmount": 25000,
    "category": "mobilya",
    "timestamp": "2026-06-10T14:30:00Z"
  }'`;

  return (
    <div className="fade-up max-w-4xl" data-testid="admin-api">
      <h1 className="font-serif text-3xl text-navy mb-1">POS Entegrasyonu & API</h1>
      <p className="text-navy/50 text-sm mb-6">Harici kasa/POS sistemlerinden güvenli satış oluşturma</p>

      {/* Global API key */}
      <Card className="p-5 mb-5" data-testid="api-key-card">
        <h2 className="font-serif text-lg text-navy mb-3 flex items-center gap-2"><KeyRound strokeWidth={1.5} className="w-4 h-4 text-gold" /> Genel API Anahtarı</h2>
        <div className="flex items-center gap-2 bg-cream border border-navy/10 rounded-md p-3">
          <code className="text-sm text-navy break-all flex-1" data-testid="api-key-value">{info.apiKey}</code>
          <button onClick={() => copy(info.apiKey)} data-testid="copy-api-key" className="p-1.5 text-navy/60 hover:text-navy active:scale-90 transition-transform"><Copy className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-navy/40 mt-2">Her istekte <code className="text-navy/60">X-API-Key</code> başlığında gönderilmelidir.</p>
      </Card>

      {/* Endpoint */}
      <Card className="p-5 mb-5">
        <h2 className="font-serif text-lg text-navy mb-3 flex items-center gap-2"><Terminal strokeWidth={1.5} className="w-4 h-4 text-gold" /> Uç Nokta</h2>
        <div className="flex items-center gap-2 bg-cream border border-navy/10 rounded-md p-3 mb-4">
          <span className="text-xs px-2 py-0.5 rounded bg-greenx/15 text-greenx font-medium">POST</span>
          <code className="text-sm text-navy break-all flex-1">{endpoint}</code>
          <button onClick={() => copy(endpoint)} className="p-1.5 text-navy/60 hover:text-navy active:scale-90 transition-transform"><Copy className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-navy/50 mb-2">Örnek istek</p>
        <pre className="bg-navy text-cream/90 text-xs rounded-md p-4 overflow-x-auto leading-relaxed" data-testid="api-sample">{sample}</pre>
        <div className="mt-3 text-xs text-navy/60 space-y-1">
          <p><strong className="text-navy">Zorunlu:</strong> <code>X-API-Key</code>, <code>X-Terminal-Token</code> (mağazaya özel), <code>storeId</code>, <code>basketAmount</code>, ve <code>code</code> veya <code>architectId</code>.</p>
          <p><strong className="text-navy">Güvenlik:</strong> Geçersiz terminal token'ı olan istekler <code>403</code>, geçersiz API anahtarı <code>401</code> döner (dolandırıcılık önleme).</p>
          <p><strong className="text-navy">Sonuç:</strong> Satış kaydedilir, müşteri indirimi uygulanır, seviye bazlı komisyon (aktif kampanya çarpanı dahil) hesaplanır.</p>
        </div>
      </Card>

      {/* Store terminal tokens */}
      <Card className="p-5" data-testid="terminal-tokens-card">
        <h2 className="font-serif text-lg text-navy mb-4">Mağaza Terminal Token'ları</h2>
        <div className="space-y-2" data-testid="store-token-list">
          {info.stores.map((s) => (
            <div key={s.id} className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 bg-cream border border-navy/10 rounded-md p-3">
              <div className="md:w-56 shrink-0">
                <p className="text-sm text-navy font-medium">{s.name}</p>
                <p className="text-[11px] text-navy/40">{s.id}</p>
              </div>
              <code className="text-xs text-navy/70 break-all flex-1" data-testid={`store-token-${s.id}`}>{s.terminalToken}</code>
              <div className="flex gap-1.5 shrink-0">
                <button onClick={() => copy(s.terminalToken)} className="p-1.5 text-navy/60 hover:text-navy active:scale-90 transition-transform"><Copy className="w-4 h-4" /></button>
                <button onClick={() => rotate(s.id, s.name)} data-testid={`rotate-token-${s.id}`} className="inline-flex items-center gap-1 text-xs border border-navy/20 text-navy px-2.5 py-1.5 rounded-md active:scale-95 transition-transform">
                  <RefreshCw className="w-3.5 h-3.5" /> Yenile
                </button>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-navy/40 mt-3">Token yenilendiğinde ilgili mağazanın POS cihazındaki token da güncellenmelidir.</p>
      </Card>
    </div>
  );
}
